import { FC, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { RxUtils, Tr } from '@utils';
import { useService } from '@hooks';
import { carListBandModelService, CarModel, dealerConfigService, FleetVehicle, simulationService, SimulationTask } from '@services';
import { IconButton, MenuItem, Stack, TextField, Typography } from '@mui/material';
import { RouterLinkButton } from '@components/common';
import { EmptyEntityPageLayout } from '@components/portal/common';
import { CarModelInfo, SelectedModelsCart, SuggestionLayout } from '@components/portal/conversion-list';
import { ReactComponent as Illustration } from '@assets/illustrations/no-vehicle-selected-for-comparison.svg';
import CloseIcon from '@mui/icons-material/Close';

const getVehicleMenuText = ({ plateNumber, carModel }: FleetVehicle) =>
	[plateNumber, [carModel?.brand, carModel?.model].filter(Boolean).join(' ')].filter(Boolean).join(' - ');

export const CarBandListSuggestions: FC = () => {
	const navigate = useNavigate();
	const { t } = useTranslation('portal');
	const { id = '' } = useParams<{ id: string }>();
	const [tasks, setTasks] = useState<SimulationTask[]>();
	const [selectedTask, setSelectedTask] = useState<SimulationTask>();
	const { data: simulationTasks = [] } = useService(() => simulationService.getLatestSimulationTasksGroupByCarListBand(), []);
	const { data: carListBandModels = [], trigger: fetchCarListBandModels } = useService(() => carListBandModelService.getAll(id), [id]);
	const { data: dealerConfig } = useService(() => dealerConfigService.find(), []);
	useEffect(() => setTasks(simulationTasks.find(({ carListBand }) => carListBand?.id === id)?.tasks), [simulationTasks]);
	useEffect(() => setSelectedTask(tasks?.[0]), [tasks]);
	const onRemoveFromCarListBandModels = (model: CarModel) => {
		const relationId = carListBandModels.find(({ carModelId }) => carModelId === model.id)?.id;
		relationId && RxUtils.promisify(carListBandModelService.delete(relationId), RxUtils.noop, RxUtils.noop, fetchCarListBandModels);
	};

	return (
		<SuggestionLayout
			task={selectedTask}
			selectedCarModels={carListBandModels.map(({ carModel }) => carModel)}
			onSelectedCarModelChange={(model, action) => {
				if (action === 'select') {
					RxUtils.promisify(
						carListBandModelService.create({ carListBandId: id, carModelId: model.id }),
						RxUtils.noop,
						RxUtils.noop,
						fetchCarListBandModels
					);
				} else if (action === 'deselect') {
					onRemoveFromCarListBandModels(model);
				}
			}}
			title={<Tr.Portal path="conversion-list.vehicle-comparison.car-band-list-suggestions.title" />}
			topRight={
				<Stack direction="row" spacing={4} alignItems="center">
					<SelectedModelsCart
						models={carListBandModels.map(({ carModel }) => carModel)}
						size="large"
						onRemove={onRemoveFromCarListBandModels}
					/>
					<IconButton sx={{ color: 'common.black' }} onClick={() => navigate(-1)}>
						<CloseIcon color="inherit" fontSize="large" />
					</IconButton>
				</Stack>
			}
			left={
				<Stack direction="column" sx={{ height: 1.0, paddingTop: 7, backgroundColor: 'grey.100' }}>
					<Stack direction="column" sx={{ paddingX: 6.5 }}>
						<Typography sx={{ marginLeft: 1.25, color: 'grey.800', fontWeight: 400 }} variant="body2">
							<Tr.Portal path="conversion-list.vehicle-comparison.car-band-list-suggestions.existing-vehicle" />
						</Typography>
						<TextField
							select
							placeholder={t('conversion-list.vehicle-comparison.car-band-list-suggestions.existing-vehicle')}
							fullWidth
							value={selectedTask?.fleetVehicle?.id || ''}
							onChange={(e) => setSelectedTask(tasks?.find(({ fleetVehicle }) => fleetVehicle?.id === e.target.value))}
						>
							{(tasks || [])
								.filter(({ fleetVehicle }) => fleetVehicle)
								.map(({ fleetVehicle }, i) => (
									<MenuItem key={`vehicle-${fleetVehicle.id}-${i}`} value={fleetVehicle.id}>
										{getVehicleMenuText(fleetVehicle)}
									</MenuItem>
								))}
						</TextField>
					</Stack>
					<Stack direction="column" sx={{ flexGrow: 1 }}>
						{selectedTask ? (
							<CarModelInfo
								mode="self"
								vehicle={selectedTask.fleetVehicle}
								vehiclePriceVisible={dealerConfig?.isVehiclePriceVisible}
								title={
									<Typography variant="h4" sx={{ fontWeight: 600, marginTop: 3, marginBottom: 4, textAlign: 'center' }}>
										<Tr.Portal path="conversion-list.vehicle-comparison.car-band-list-suggestions.consumption" />
									</Typography>
								}
							/>
						) : (
							<EmptyEntityPageLayout
								sx={{ paddingX: 6.5 }}
								illustration={<Illustration />}
								content={
									<Tr.Portal path="conversion-list.vehicle-comparison.car-band-list-suggestions.empty-selection-text" />
								}
							/>
						)}
					</Stack>
				</Stack>
			}
			cta={
				<RouterLinkButton
					data-testid="car-band-list-assignments-navigation-button"
					disabled={!carListBandModels.length}
					href={`../car-band-list-assignments/${id}`}
					variant="contained"
					color="primary"
					sx={{ width: 200 }}
				>
					<Tr.Portal path="next" />
				</RouterLinkButton>
			}
		/>
	);
};
